import { useCallback, useState, type ReactNode } from "react";
import {
  useAvailableActivities,
  useSendActivity,
  type AvailableActivity,
  type DeepThoughtsPrerequisitesStatus,
} from "@/lib/api/activities";
import { ConfirmActionDialog } from "@/components/ui/confirm-action-dialog";
import { useDeepThoughtsSendStore } from "@/lib/hooks/conversations/use-deep-thoughts-send-store";
import { usePostHogEvent } from "@/lib/posthog";
import { ConnectMomentSetupDialog } from "./connect-moment/connect-moment-setup-dialog";
import { DeepThoughtsSendHost } from "./deep-thoughts/deep-thoughts-send-host";
import { HotTakesDialogHost } from "./hot-takes";

interface UseActivitySendControllerParams {
  mentorshipId: string;
  teenName: string;
  // false = keep Deep Thoughts open-state local instead of the global store,
  // so a second host doesn't render the dialog twice.
  sharedDeepThoughtsStore?: boolean;
}

export interface UseActivitySendControllerResult {
  selectActivity: (activity: AvailableActivity) => void;
  dialogs: ReactNode;
}

export function useActivitySendController({
  mentorshipId,
  teenName,
  sharedDeepThoughtsStore = true,
}: UseActivitySendControllerParams): UseActivitySendControllerResult {
  const [confirmActivity, setConfirmActivity] =
    useState<AvailableActivity | null>(null);
  const [showSameDayWarning, setShowSameDayWarning] = useState(false);
  const [hotTakesPickerOpen, setHotTakesPickerOpen] = useState(false);
  const [connectSetupOpen, setConnectSetupOpen] = useState(false);

  const [localDeepThoughtsOpen, setLocalDeepThoughtsOpen] = useState(false);
  const [localDeepThoughtsPrereqs, setLocalDeepThoughtsPrereqs] =
    useState<DeepThoughtsPrerequisitesStatus | null>(null);
  const storeDeepThoughtsOpen = useDeepThoughtsSendStore((s) => s.isOpen);
  const storeDeepThoughtsPrereqs = useDeepThoughtsSendStore((s) => s.prereqs);
  const storeSetDeepThoughtsOpen = useDeepThoughtsSendStore((s) => s.setOpen);
  const storeOpenDeepThoughts = useDeepThoughtsSendStore((s) => s.openSend);

  const deepThoughtsOpen = sharedDeepThoughtsStore
    ? storeDeepThoughtsOpen
    : localDeepThoughtsOpen;
  const deepThoughtsPrereqs = sharedDeepThoughtsStore
    ? storeDeepThoughtsPrereqs
    : localDeepThoughtsPrereqs;
  const setDeepThoughtsOpen = sharedDeepThoughtsStore
    ? storeSetDeepThoughtsOpen
    : setLocalDeepThoughtsOpen;

  const openDeepThoughts = useCallback(
    (prereqs: DeepThoughtsPrerequisitesStatus | null) => {
      if (sharedDeepThoughtsStore) {
        storeOpenDeepThoughts(prereqs);
      } else {
        setLocalDeepThoughtsPrereqs(prereqs);
        setLocalDeepThoughtsOpen(true);
      }
    },
    [sharedDeepThoughtsStore, storeOpenDeepThoughts],
  );

  const { data: availability } = useAvailableActivities(mentorshipId);
  const sendMutation = useSendActivity();
  const { dispatchEvent } = usePostHogEvent("activity.mentor");

  const selectActivity = useCallback(
    (activity: AvailableActivity) => {
      dispatchEvent("activity.mentor.type_selected", {
        mentorshipId,
        activityType: activity.type,
      });

      if (!activity.enabled) {
        dispatchEvent("activity.mentor.blocked", {
          mentorshipId,
          reason: activity.reason,
        });
        return;
      }

      if (activity.type === "hot_takes") {
        setHotTakesPickerOpen(true);
        dispatchEvent("activity.mentor.compose_opened", { mentorshipId });
        return;
      }

      if (activity.type === "deep_thoughts") {
        dispatchEvent("activity.mentor.compose_opened", { mentorshipId });
        openDeepThoughts(activity.prereqs ?? null);
        return;
      }

      if (activity.type === "connect_moment") {
        setConnectSetupOpen(true);
        dispatchEvent("activity.mentor.compose_opened", { mentorshipId });
        return;
      }

      if (availability?.sentToday) {
        dispatchEvent("activity.mentor.same_day_warning_shown", {
          mentorshipId,
        });
        setShowSameDayWarning(true);
      }

      setConfirmActivity(activity);
    },
    [availability, mentorshipId, dispatchEvent, openDeepThoughts],
  );

  const handleConfirmSend = useCallback(() => {
    if (!confirmActivity) return;

    if (showSameDayWarning) {
      dispatchEvent("activity.mentor.same_day_warning_confirmed", {
        mentorshipId,
        confirmed: true,
      });
    }

    sendMutation.mutate(
      { mentorshipId, activityType: confirmActivity.type },
      {
        onSuccess: () => {
          dispatchEvent("activity.mentor.sent", {
            mentorshipId,
            activityType: confirmActivity.type,
          });
          setConfirmActivity(null);
          setShowSameDayWarning(false);
        },
        onError: () => {
          setConfirmActivity(null);
          setShowSameDayWarning(false);
        },
      },
    );
  }, [
    confirmActivity,
    showSameDayWarning,
    mentorshipId,
    sendMutation,
    dispatchEvent,
  ]);

  const handleCancelSend = useCallback(() => {
    if (showSameDayWarning) {
      dispatchEvent("activity.mentor.same_day_warning_confirmed", {
        mentorshipId,
        confirmed: false,
      });
    }
    setConfirmActivity(null);
    setShowSameDayWarning(false);
  }, [showSameDayWarning, mentorshipId, dispatchEvent]);

  const dialogs = (
    <>
      <ConfirmActionDialog
        isOpen={!!confirmActivity}
        onClose={handleCancelSend}
        onConfirm={handleConfirmSend}
        title={
          showSameDayWarning
            ? "Activity already sent today"
            : `Send ${confirmActivity?.label}?`
        }
        description={
          showSameDayWarning
            ? `You've already sent an activity to ${teenName} today. Send another?`
            : `This will send a ${confirmActivity?.label} to ${teenName}.`
        }
        loading={sendMutation.isPending}
        confirmText="Send"
        confirmVariant="default"
      />

      <HotTakesDialogHost
        open={hotTakesPickerOpen}
        mentorshipId={mentorshipId}
        teenName={teenName}
        onOpenChange={setHotTakesPickerOpen}
      />

      <DeepThoughtsSendHost
        isOpen={deepThoughtsOpen}
        prereqs={deepThoughtsPrereqs}
        onOpenChange={setDeepThoughtsOpen}
        mentorshipId={mentorshipId}
        teenName={teenName}
        onOpenHotTakes={() => setHotTakesPickerOpen(true)}
        onOpenConnectionCards={() => setConnectSetupOpen(true)}
      />

      <ConnectMomentSetupDialog
        open={connectSetupOpen}
        onOpenChange={setConnectSetupOpen}
        mentorshipId={mentorshipId}
        teenName={teenName}
      />
    </>
  );

  return { selectActivity, dialogs };
}
